/* eslint-disable no-shadow, react-redux/mapStateToProps-prefer-parameters-names */
import React, {
    Component,
} from 'react';

import PropTypes from 'prop-types';
import {
    connect,
} from 'react-redux';

import Footer from 'wanderu-component-footer';
import Intercom from 'wanderu-component-intercom';
import NotFound from 'wanderu-component-not-found';
import { BusLoader } from 'wanderu-components';
import {
    carrierGuideSelectors,
    carrierGuideShapes as shapes,
    fetchCarrierGuideAction,
} from 'wanderu-ducks-seo';
import {
    splitTestSelectors,
    splitTestActions,
} from 'wanderu-ducks-split-test';
import {
    localize,
    i18nCoordinator,
    selectLocale,
} from 'wanderu-i18n';
import Widget from 'wanderu-widget';

import cgConfig from '../../../config/carrierguide.locale';
import CarrierGuide from '../../components/CarrierGuide/CarrierGuide';
import CarrierGuideMeta from '../../components/CarrierGuide/CarrierGuideMeta/CarrierGuideMeta';
import {
    generateLocaleAwareGuideId,
} from '../../utils';
import { localizeStructuredData } from '../utils';

function _getCanonicalPath(locale, canonicalSlug) {
    return `/${locale}/carrier/${canonicalSlug}/`;
}

class CarrierGuideContainer extends Component {
    static propTypes = {
        carrier: shapes.carrierShape,
        isLoaded: PropTypes.bool.isRequired,
        stats: shapes.statsShape,
        quickviewMetrics: shapes.quickviewShape,
    };

    static async serverFetchData(opts) {
        const {
            dispatch,
            i18nCoordinator,
            params,
            getState,
        } = opts;

        const {
            carrierGuideId,
            locale,
        } = params;

        const cachedConfigResponse = splitTestSelectors.selectTestData(getState());
        dispatch(splitTestActions.setCachedTestParams(cachedConfigResponse));

        const localeAwareCarrierGuideId = generateLocaleAwareGuideId(params);

        await Promise.all([
            Widget.serverFetchData(opts),
            dispatch(
                fetchCarrierGuideAction.fetchCarrierGuide(
                    localeAwareCarrierGuideId,
                    carrierGuideId,
                    locale,
                ),
            ),
        ]);

        await i18nCoordinator.registerTranslatableApp({
            ...cgConfig,
            translationFetchFn: () => {
                const localeAwareCarrierGuideId = generateLocaleAwareGuideId(params);
                const apiTranslations = carrierGuideSelectors.selectContent(
                    getState(),
                    localeAwareCarrierGuideId,
                );

                return {
                    ...apiTranslations,
                };
            },
        });
    }

    async componentDidMount() {
        const {
            match,
            isLoaded,
            localeAwareCarrierGuideId,
        } = this.props;
        const {
            locale,
        } = match.params;

        if (!isLoaded) {
            this.props.fetchCarrierGuide(
                localeAwareCarrierGuideId,
                match.params.carrierGuideId,
                locale,
            );
        }

        await i18nCoordinator.registerTranslatableApp({
            ...cgConfig,
            translationFetchFn: async () => {
                return undefined;
            },
        });
    }

    async componentDidUpdate(prevProps) {
        const {
            match,
            content,
            localeAwareCarrierGuideId,
        } = this.props;
        const {
            carrierGuideId,
            locale,
        } = match.params;

        const previousLocaleAwareCarrierGuideId = prevProps.localeAwareCarrierGuideId;

        if (previousLocaleAwareCarrierGuideId !== localeAwareCarrierGuideId) {
            this.props.fetchCarrierGuide(localeAwareCarrierGuideId, carrierGuideId, locale);
        }

        if (prevProps.isFetching && !this.props.isFetching) {
            i18nCoordinator.updateTranslations(locale, content, cgConfig.namespace);
            // the translations changed, but there's nothing that will cause a re-render,
            // so we force it.
            this.forceUpdate();
        }
    }

    componentWillUnmount() {
        i18nCoordinator.unregisterTranslatableApp(cgConfig.namespace);
        this.props.clearCarrierGuide(this.props.localeAwareCarrierGuideId);
    }

    render() {
        const {
            content,
            isFetching,
            locale,
            noIndex,
            noIndexLocale,
            notFound,
            error,
            stats,
            staticContext,
            structuredData,
            t,
            i18nMoney,
        } = this.props;

        if (staticContext) {
            staticContext.cache = {
                secondsFromNow: 60 * 60 * 24 * 7,
            };

            if (staticContext.url) {
                return null;
            }

            if (notFound) {
                staticContext.status = 404;
            } else if (error) {
                staticContext.status = error.status;
                staticContext.cache = undefined;
            }
        }

        if (notFound || error) {
            return <NotFound status={staticContext?.status || error?.status} />;
        }

        if (isFetching || !content) {
            return (
                <BusLoader large />
            );
        }

        let minPriceUsd;
        if (stats && stats.pricing) {
            minPriceUsd = stats.pricing.minUsd;
        }

        const localizedStructuredData = localizeStructuredData({
            structuredData,
            t,
            i18nMoney,
            locale,
        });

        return (
            <>
                <CarrierGuideMeta
                    canonicalPath={_getCanonicalPath(locale, content.canonicalSlug)}
                    minPriceUsd={minPriceUsd}
                    noIndex={noIndex}
                    noIndexLocale={noIndexLocale}
                    structuredData={localizedStructuredData}
                />
                <CarrierGuide
                    {...this.props}
                    minPriceUsd={minPriceUsd}
                />
                <Footer
                    limitLinkFlow
                />
                <Intercom />
            </>
        );
    }
}

CarrierGuideContainer.propTypes = {
    carrier: shapes.carrierShape,
    clearCarrierGuide: PropTypes.func.isRequired,
    content: PropTypes.object,
    error: PropTypes.object,
    fetchCarrierGuide: PropTypes.func.isRequired,
    i18nMoney: PropTypes.func.isRequired,
    isFetching: PropTypes.bool,
    isLoaded: PropTypes.bool.isRequired,
    locale: PropTypes.string,
    localeAwareCarrierGuideId: PropTypes.string,
    match: PropTypes.shape({
        params: PropTypes.shape({
            carrierGuideId: PropTypes.string.isRequired,
            locale: PropTypes.string.isRequired,
        }).isRequired,
    }).isRequired,
    noIndex: PropTypes.bool,
    noIndexLocale: PropTypes.bool,
    notFound: PropTypes.bool,
    quickviewMetrics: shapes.quickviewShape,
    staticContext: PropTypes.object,
    stats: shapes.statsShape,
    structuredData: PropTypes.object,
    t: PropTypes.func.isRequired,
};

function mapStateToProps(appState, ownProps) {
    const carrierGuideId = generateLocaleAwareGuideId(ownProps.match.params);

    const error = fetchCarrierGuideAction.selectError(appState, carrierGuideId);
    const notFound = fetchCarrierGuideAction.selectNotFound(appState, carrierGuideId);

    if (notFound || error) {
        return {
            notFound,
            error,
        };
    }

    return {
        localeAwareCarrierGuideId: carrierGuideId,
        carrier: carrierGuideSelectors.selectCarrier(appState, carrierGuideId),
        content: carrierGuideSelectors.selectContent(appState, carrierGuideId),
        infoSectionContent:
            carrierGuideSelectors.selectInfoSectionContent(appState, carrierGuideId),
        isFetching: fetchCarrierGuideAction.selectWorking(appState, carrierGuideId),
        isLoaded: fetchCarrierGuideAction.selectSuccess(appState, carrierGuideId),
        locale: selectLocale(appState),
        longFormContent: carrierGuideSelectors.selectLongFormContent(appState, carrierGuideId),
        noIndex: carrierGuideSelectors.selectNoIndex(appState, carrierGuideId),
        noIndexLocale: carrierGuideSelectors.selectNoIndexLocale(appState, carrierGuideId),
        notFound: fetchCarrierGuideAction.selectNotFound(appState, carrierGuideId),
        quickviewMetrics: carrierGuideSelectors.selectQuickviewMetrics(
            appState,
            carrierGuideId,
        ),
        stats: carrierGuideSelectors.selectStats(appState, carrierGuideId),
        structuredData: carrierGuideSelectors.selectStructuredData(appState, carrierGuideId),
        carrierReviews:
            carrierGuideSelectors.selectSimplifiedCarrierReviews(appState, carrierGuideId),
        travelReviews: carrierGuideSelectors.selectTravelReviews(appState, carrierGuideId),
        reviewSummary: carrierGuideSelectors.selectReviewSummary(appState, carrierGuideId),
        showModal: carrierGuideSelectors.selectConditionalModal(appState, carrierGuideId),
        interlinks: carrierGuideSelectors.selectInterlinks(appState, carrierGuideId),
        bestFeatures: carrierGuideSelectors.selectBestFeatures(appState, carrierGuideId),
        blurbSectionContent:
            carrierGuideSelectors.selectBlurbSectionContent(appState, carrierGuideId),
        renderFlags: carrierGuideSelectors.selectRenderFlags(appState, carrierGuideId),
        topRoutes: carrierGuideSelectors.selectTopRoutes(appState, carrierGuideId),
    };
}

const mapDispatchToProps = {
    clearCarrierGuide: fetchCarrierGuideAction.clearCarrierGuide,
    fetchCarrierGuide: fetchCarrierGuideAction.fetchCarrierGuide,
};

export {
    CarrierGuideContainer,
    mapStateToProps,
};

const localizedCarrierGuideContainer = localize(CarrierGuideContainer, cgConfig.namespace);
export default connect(mapStateToProps, mapDispatchToProps)(
    localizedCarrierGuideContainer,
);
